import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LEGAL_NOTE } from "@/components/landing/legalNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 transition-colors duration-300";

export const CitaButton = ({ testId = "footer-pide-cita-btn", label = "Concertar Cita Previa" }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", comunidad: "", preferencia: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/appointment`, form);
      toast.success("Solicitud de cita enviada. Le confirmaremos la fecha por email.");
      setForm({ nombre: "", email: "", comunidad: "", preferencia: "" });
      setOpen(false);
    } catch {
      toast.error("No se pudo enviar la solicitud. Inténtelo de nuevo en unos minutos.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid={testId}
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center gap-2 border border-white/25 text-white text-sm font-semibold rounded-lg px-6 py-4 hover:bg-white hover:text-slate-900 transition-colors duration-300 w-full sm:w-auto"
      >
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cita-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Pide tu cita</DialogTitle>
            <DialogDescription>
              Atención presencial exclusivamente con cita previa. Le confirmaremos la fecha por email.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} data-testid="cita-form" className="mt-2 space-y-4">
            <input data-testid="cita-input-nombre" required placeholder="Nombre y apellidos" value={form.nombre} onChange={set("nombre")} className={inputCls} />
            <input type="email" data-testid="cita-input-email" required placeholder="Correo electrónico" value={form.email} onChange={set("email")} className={inputCls} />
            <input data-testid="cita-input-comunidad" placeholder="Comunidad / Edificio (opcional)" value={form.comunidad} onChange={set("comunidad")} className={inputCls} />
            <input data-testid="cita-input-preferencia" placeholder="Preferencia de fecha y franja horaria" value={form.preferencia} onChange={set("preferencia")} className={inputCls} />
            <label className="flex items-start gap-3 text-xs text-slate-500 leading-relaxed cursor-pointer" data-testid="cita-privacidad-label">
              <input type="checkbox" required data-testid="cita-input-privacidad" className="mt-0.5 accent-slate-900" />
              <span>
                He leído y acepto la{" "}
                <Link to="/politica-de-privacidad" className="underline underline-offset-2 hover:text-slate-900 transition-colors duration-300">
                  Política de Privacidad
                </Link>
                .
              </span>
            </label>
            <button
              type="submit"
              disabled={sending}
              data-testid="cita-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-3.5 rounded-lg transition-colors duration-300"
            >
              {sending ? (<>Enviando <Loader2 className="w-4 h-4 animate-spin" /></>) : "Solicitar cita previa"}
            </button>
            <p data-testid="cita-legal-note" className="text-[11px] leading-relaxed text-slate-400">{LEGAL_NOTE}</p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
